import { useState, useRef } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Upload, X, Users, Image, Video, Plus, Trash2, FolderOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

const addTeamMediaSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  departmentId: z.number({
    required_error: "Please select a department",
  }),
});

type AddTeamMediaForm = z.infer<typeof addTeamMediaSchema>;

export default function AddTeamImage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch departments for selection
  const { data: departmentsData } = useQuery({
    queryKey: ['/admin/departments'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/departments");
      return response.json();
    },
  }) as { data: any[] | undefined };

  const form = useForm<AddTeamMediaForm>({
    resolver: zodResolver(addTeamMediaSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  const addTeamMediaMutation = useMutation({
    mutationFn: async (data: AddTeamMediaForm & { files: FileList }) => {
      // Upload each file to the existing team-images API
      const uploadPromises = Array.from(data.files).map(async (file) => {
        const departmentName = departmentsData?.find(dept => dept.id === data.departmentId)?.name || 'unknown';
        const fileUrl = await uploadFileToFirebase(file, departmentName);
        
        const fileData = {
          title: `${data.title} - ${file.name}`,
          description: data.description,
          departmentId: data.departmentId,
          mediaUrl: fileUrl,
          fileName: file.name,
          fileSize: file.size,
        };
        
        const response = await apiRequest("POST", "/team-images", fileData);
        return response.json();
      });
      
      return Promise.all(uploadPromises);
    },
    onSuccess: (results) => {
      toast({
        title: "Success! 🎉",
        description: `${results.length} media files uploaded successfully!`,
        variant: "default",
      });
      queryClient.invalidateQueries({ queryKey: ['/team-images'] });
      setLocation('/team-images');
    },
    onError: (error: Error) => {
      console.error("Error uploading team media:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setIsUploading(false);
      setUploadProgress(0);
    },
  });

  // Multi-file upload functions
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Validate file types and sizes
      const validFiles = Array.from(files).filter(file => {
        const isValidType = file.type.startsWith('image/') || file.type.startsWith('video/');
        const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB limit
        
        if (!isValidType) {
          toast({
            title: "Invalid File Type",
            description: `${file.name} is not a valid image or video file`,
            variant: "destructive",
          });
          return false;
        }
        
        if (!isValidSize) {
          toast({
            title: "File Too Large",
            description: `${file.name} exceeds 50MB limit`,
            variant: "destructive",
          });
          return false;
        }
        
        return true;
      });

      if (validFiles.length > 10) {
        toast({
          title: "Too Many Files",
          description: "Maximum 10 files allowed per upload",
          variant: "destructive",
        });
        return;
      }

      if (validFiles.length > 0) {
        // Create a new FileList with valid files
        const dt = new DataTransfer();
        validFiles.forEach(file => dt.items.add(file));
        setSelectedFiles(dt.files);
        
        console.log(`${validFiles.length} files selected for upload`);
      }
    }
  };

  const removeFile = (index: number) => {
    if (selectedFiles) {
      const dt = new DataTransfer();
      Array.from(selectedFiles).forEach((file, i) => {
        if (i !== index) dt.items.add(file);
      });
      setSelectedFiles(dt.files.length > 0 ? dt.files : null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadFileToFirebase = async (file: File, departmentName: string): Promise<string> => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      console.log("Starting Firebase upload for team image:", file.name);
      
      // Import Firebase functions dynamically
      const { storage, ensureAuthenticated } = await import('@/lib/firebase');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      
      // Ensure user is authenticated with Google OAuth
      await ensureAuthenticated();
      
      // Create a storage reference in team-images folder with department subfolder
      const timestamp = Date.now();
      const fileName = `team-images/${departmentName}/${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      console.log('Uploading to Firebase Storage:', fileName);
      setUploadProgress(25);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type
      });
      
      console.log('Upload successful, getting download URL...');
      setUploadProgress(75);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Download URL obtained:', downloadURL);
      setUploadProgress(100);
      
      return downloadURL;
    } catch (error) {
      console.error("Firebase upload error:", error);
      
      let errorMessage = "Failed to upload image. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes('auth')) {
          errorMessage = "Please sign in with Google to upload images.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Upload Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const onSubmit = async (data: AddTeamMediaForm) => {
    try {
      if (!selectedFiles || selectedFiles.length === 0) {
        toast({
          title: "No Files Selected",
          description: "Please select at least one image or video to upload",
          variant: "destructive",
        });
        return;
      }

      // Start upload process
      setIsUploading(true);
      setUploadProgress(0);

      // Upload all files using the mutation
      await addTeamMediaMutation.mutateAsync({
        ...data,
        files: selectedFiles,
      });
    } catch (error) {
      console.error("Team media upload failed:", error);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation('/admin')}
            className="text-gray-600 hover:text-gray-900"
            data-testid="button-back-to-admin"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin Dashboard
          </Button>
        </div>

        {/* Form Card */}
        <Card className="shadow-xl shadow-servify-purple/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-gray-900 flex items-center">
              <FolderOpen className="mr-3 h-6 w-6 text-servify-purple" />
              Add Team Media
            </CardTitle>
            <p className="text-gray-600 text-sm">Upload multiple images and videos for a specific department to be displayed in Meet the Team section.</p>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Media Details</h3>
                  
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">Media Collection Title</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            placeholder="e.g., Engineering Team Event, HR Team Building, Marketing Campaign"
                            data-testid="input-image-title"
                            className="h-10 border-gray-300 focus:border-servify-purple focus:ring-1 focus:ring-servify-purple" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">Description (Optional)</FormLabel>
                        <FormControl>
                          <Textarea 
                            {...field} 
                            placeholder="Brief description about this media collection"
                            data-testid="textarea-image-description"
                            className="border-gray-300 focus:border-servify-purple focus:ring-1 focus:ring-servify-purple"
                            rows={3}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="departmentId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">Department</FormLabel>
                        <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                          <FormControl>
                            <SelectTrigger 
                              data-testid="select-department"
                              className="w-full h-11 border border-gray-300 rounded-lg bg-white hover:border-servify-purple focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 transition-all duration-200 shadow-sm"
                            >
                              <SelectValue placeholder="Select a department" className="text-gray-700" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                            {departmentsData?.map((department) => (
                              <SelectItem 
                                key={department.id} 
                                value={department.id.toString()}
                                className="px-3 py-2 text-sm text-gray-700 hover:bg-servify-purple/10 hover:text-servify-purple focus:bg-servify-purple/10 focus:text-servify-purple cursor-pointer transition-colors"
                              >
                                {department.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Multi-File Upload */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Team Media Upload</h3>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-servify-purple transition-colors">
                    <FolderOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <div className="space-y-2">
                      <p className="text-lg font-medium text-gray-700">Upload Media Files</p>
                      <p className="text-sm text-gray-500">Select multiple images and videos (up to 10 files, 50MB each)</p>
                      <p className="text-xs text-gray-400">Supported: JPG, PNG, GIF, WebP, MP4, WebM, OGG</p>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleFileSelect}
                        multiple
                        ref={fileInputRef}
                        className="hidden"
                        id="media-upload"
                        data-testid="input-media-files"
                      />
                      <label
                        htmlFor="media-upload"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Choose Files
                      </label>
                    </div>
                  </div>

                  {/* Selected Files Display */}
                  {selectedFiles && selectedFiles.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">
                          Selected Files ({selectedFiles.length})
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedFiles(null)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="mr-1 h-3 w-3" />
                          Clear All
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                        {Array.from(selectedFiles).map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div className="flex items-center space-x-3">
                              {file.type.startsWith('image/') ? (
                                <Image className="h-5 w-5 text-blue-500" />
                              ) : (
                                <Video className="h-5 w-5 text-purple-500" />
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900 truncate max-w-48">
                                  {file.name}
                                </p>
                                <div className="flex items-center space-x-2">
                                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                  <Badge variant="outline" className="text-xs">
                                    {file.type.startsWith('image/') ? 'Image' : 'Video'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload Progress */}
                  {isUploading && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">Uploading files...</p>
                        <p className="text-sm text-gray-500">{uploadProgress}%</p>
                      </div>
                      <Progress value={uploadProgress} className="h-2" />
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex items-center space-x-4 pt-6">
                  <Button
                    type="submit"
                    disabled={addTeamMediaMutation.isPending || isUploading || !selectedFiles || selectedFiles.length === 0}
                    className="bg-servify-purple hover:bg-servify-purple/90 text-white font-medium py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                    data-testid="button-submit-team-image"
                  >
                    {addTeamMediaMutation.isPending || isUploading ? (
                      "Uploading..."
                    ) : (
                      "Upload Team Media"
                    )}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation('/team-management')}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                    data-testid="button-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}